import { useQuery, QueryStatus } from "@tanstack/react-query";
import { Crosshair } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { getScoreBarColor } from "@/lib/chart";
import { message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type CreativesScorecardParams } from "@/apis/brand-os/brand-governance";
import type { CreativesScorecardData, DimensionScore } from "@/schema/brand-os/brand-governance";


function dimensions(dimension_summary: NonNullable<CreativesScorecardData["dimension_summary"]>): DimensionScore[] {
  return Object.entries(dimension_summary).map(([key, metric]) => ({
    id: key,
    label: key.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
    achieved: Math.round(metric.score ?? 0),
    total: 100,
  }));
}

function DimensionContent({ data }: { data: CreativesScorecardData }) {
  const rows = data.dimension_summary ? dimensions(data.dimension_summary) : [];

  if (!rows.length) return <LockedContent message={message} />;

  return rows.map((item) => <DimensionRow key={item.id} item={item} />);
}

function DimensionComponent({ status, data }: { status: QueryStatus; data?: CreativesScorecardData }) {
  switch (status) {
    case "pending":
      return <DimensionRowSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data ? <DimensionContent data={data} /> : <LockedContent message={message} />;
  }
}

function DimensionRow({ item }: { item: DimensionScore }) {
  const percent = item.total > 0 ? (item.achieved / item.total) * 100 : 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground/70">{item.label}</span>
        <span className="text-xs font-medium text-foreground/40">{item.achieved}%</span>
      </div>
      <Progress value={percent} className="h-2 w-full bg-muted" indicatorClassName={getScoreBarColor(percent)} />
    </div>
  );
}

export function Dimension({ params }: { params: CreativesScorecardParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.CreativesScorecard(params));

  return (
    <Card className="col-span-1 gap-3">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex size-9 items-center justify-center rounded-lg bg-muted">
            <Crosshair className="size-5 text-muted-foreground" />
          </div>
          <CardTitle>By Dimension</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col space-y-4">
        <DimensionComponent status={status} data={data?.data} />
      </CardContent>
    </Card>
  );
}

function DimensionRowSkeleton() {
  return (
    <div className="space-y-5">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-4 w-10 rounded-md" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}
